/** Typed data hooks. One place per endpoint, so a screen never builds a URL.
 *
 * Staleness is set per query rather than globally, which is the reason ADR-0027 chose TanStack
 * Query over hand-rolled fetching:
 *
 * - **A receipt never goes stale.** A calculation is immutable; a recalculation produces a new
 *   row with a new id. So a receipt can be cached indefinitely, and a drawer reopens instantly.
 * - **Totals and figures are recomputed**, so they are refetched on mount. A stale total beside a
 *   fresh receipt is the failure this product can least afford.
 *
 * **No `org_id` anywhere.** These used to take one, because the demo router did; the authenticated
 * endpoints take none. The organisation comes from the session, so a client cannot ask for another
 * one — which means it is not a cache key either, and switching workspaces clears the cache rather
 * than keying round it.
 */

import {
  keepPreviousData,
  skipToken,
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useRef } from "react";

import { api, apiFetch, apiUrl, keys } from "./client";
import { sumDecimals } from "../lib/decimal";
import { isPeriodClosed, refusalFrom, Refused } from "./refusal";
import type { components } from "./schema";

export type Organization = components["schemas"]["OrganizationOut"];
export type Membership = components["schemas"]["MembershipOut"];
export type Inventory = components["schemas"]["InventoryOut"];
export type InventoryMonthly = components["schemas"]["InventoryMonthlyOut"];
export type InventoryMonth = components["schemas"]["InventoryMonthOut"];
export type Figure = components["schemas"]["FigureOut"];
export type Receipt = components["schemas"]["ReceiptOut"];
export type Obligation = components["schemas"]["ObligationOut"];
export type Reason = components["schemas"]["ReasonOut"];
export type Report = components["schemas"]["ReportOut"];
export type ReportLine = components["schemas"]["ReportLineOut"];
export type Finding = components["schemas"]["FindingOut"];
export type ReportContext = components["schemas"]["ContextOut"];
export type Scope2Method = "location" | "market";

/** A reporting window, as every query here takes one.
 *
 * **`WINDOW` is gone.** It was `{period_start: "2025-01-01", period_end: "2025-12-31"}` and every
 * main query used it, so the Californian subsidiary's **March** year end could not be looked at and
 * a second annual cycle needed a source edit — finding **F11**. The window now comes from
 * `useScreen`, which reads it from the URL or from the workspace's own reporting periods.
 */
export interface Window {
  period_start: string;
  period_end: string;
}

/** The answer, or a `Refused` carrying the status and the code.
 *
 * **It used to `String(result.error.detail)`**, which on this API's refusal shape — a `detail`
 * object — produces the literal `[object Object]`. So a route that had taken the trouble to write a
 * sentence for the reader was rendered as six words of JavaScript, and the status that would have
 * told a screen whether to offer a retry was dropped on the floor. `imported.ts` says so in its own
 * header and worked around it locally; this is the fix at the source.
 *
 * `response` is required, so a caller cannot accidentally produce a refusal with no status — which
 * `Refused` reads as *"we do not know whether it was written"*.
 */
export function unwrap<T>(result: { data?: T; error?: unknown; response: Response }): T {
  if (result.error !== undefined || result.data === undefined) {
    throw refusalFrom(result.response, result.error, "The API request failed.");
  }
  return result.data;
}

/** The organisation this session is in. Singular, because a session names one. */
export function useOrganization() {
  return useQuery({
    queryKey: keys.organization(),
    queryFn: async () => unwrap(await api.GET("/api/v1/organization", {})),
    staleTime: 60_000,
  });
}

/** Every organisation this person belongs to, for the workspace switcher.
 *
 * A separate question from `useOrganization`, and answered by a different endpoint for a reason
 * worth knowing: it spans organisations, which a tenant-scoped query cannot do. It carries a name
 * and a role and no data.
 */
export function useMemberships() {
  return useQuery({
    queryKey: keys.memberships(),
    queryFn: async () => unwrap(await api.GET("/api/v1/memberships", {})),
    staleTime: 60_000,
  });
}

/** The authoritative totals for a window — **and no request at all without one.**
 *
 * `window` is `undefined` when the workspace has no reporting period and the URL names none
 * (`useScreen`), and a total for no period is not a question the API can answer truthfully: it used
 * to be asked with a 1970 sentinel and answered `200`, an empty inventory for a year in which
 * nothing could have happened. `skipToken` is TanStack's own spelling of *this query cannot run
 * yet* — it is typed, so `queryFn` is never called with a window it does not have, and the query
 * stays `pending` rather than succeeding with nothing. Callers therefore branch on the window
 * **before** they read `isPending`, or they wait on a request that will never be made.
 *
 * **Only the two dates are sent.** The caller's `Window` carries a `label` for the top bar, and
 * spreading it put `label=…` in every query string this hook ever issued.
 */
export function useInventory(
  window: Window | undefined,
  method: Scope2Method,
  options: {
    /** Also ask for the traceability summary — the six-link assessment of every figure, reduced
     * to a count and the commonest break. Opt-in because it costs a pass over the window's
     * evidence, and most screens do not state it. */
    evidence?: boolean;
  } = {},
) {
  const evidence = options.evidence === true;
  return useQuery({
    queryKey: keys.inventory(
      window?.period_start ?? "none",
      window?.period_end ?? "none",
      method,
      evidence,
    ),
    queryFn: window
      ? async () =>
          unwrap(
            await api.GET("/api/v1/inventory", {
              params: {
                query: {
                  period_start: window.period_start,
                  period_end: window.period_end,
                  scope2_method: method,
                  ...(evidence ? { evidence: true } : {}),
                },
              },
            }),
          )
      : skipToken,
  });
}

/** A window's emissions month by month (ADR-0110), reconciling exactly to `useInventory`'s total.
 *
 * **Not issued without a window**, for `useInventory`'s reason. **It cannot succeed with nothing**:
 * `unwrap` throws on an error or an empty body, so a dropped request is a failed query that
 * `ReadFailed` can say so about, never an empty series that draws as a year with no emissions.
 * Left at the default `staleTime` of zero and refetched on mount, like the totals it must agree
 * with - a cached series beside a fresh total is the mismatch the chart's reconciliation check
 * exists to catch, and it is better never to produce one.
 */
export function useInventoryMonthly(
  window: Window | undefined,
  method: Scope2Method,
  /** One site's series (Batch 5's site page). Absent is the workspace's, which is a different key:
   * a site's months must never be served from the workspace's cache entry or the other way round. */
  facilityId?: string,
) {
  return useQuery({
    queryKey: keys.inventoryMonthly(
      window?.period_start ?? "none",
      window?.period_end ?? "none",
      method,
      facilityId,
    ),
    queryFn: window
      ? async () => {
          const series = unwrap(
            await api.GET("/api/v1/inventory/monthly", {
              params: {
                query: {
                  period_start: window.period_start,
                  period_end: window.period_end,
                  scope2_method: method,
                  ...(facilityId ? { facility_id: facilityId } : {}),
                },
              },
            }),
          );
          // A 200 whose body is not a series (a proxy's page, an older server) is a failed read,
          // not an empty one: `months` missing must never be drawn as a year with no months.
          if (!Array.isArray((series as { months?: unknown }).months)) {
            throw new Error("The monthly series came back in a shape this screen cannot read.");
          }
          return series;
        }
      : skipToken,
  });
}

/** The totals for several windows at once - the periods of a trend (ADR-0102).
 *
 * **The same query key as `useInventory`**, so the window already on screen is a cache hit and a
 * trend over four periods costs three requests, not four. Results are in the order of `windows`,
 * and each keeps its own pending and error state: a period that failed to read is a sentence in its
 * own column, not a reason to draw the others as if the series were whole.
 *
 * **`staleTime` is set, and that is what makes the key shared in practice.** A query mounted after
 * another observer of the same key has already filled the cache is, at the default staleTime of
 * zero, stale on arrival, and refetches: the prior period the KPI strip had just read was asked
 * for again by the trend beside it.
 */
export function useInventories(windows: readonly Window[], method: Scope2Method) {
  return useQueries({
    queries: windows.map((window) => ({
      queryKey: keys.inventory(window.period_start, window.period_end, method),
      staleTime: 60_000,
      queryFn: async () =>
        unwrap(
          await api.GET("/api/v1/inventory", {
            params: {
              query: {
                period_start: window.period_start,
                period_end: window.period_end,
                scope2_method: method,
              },
            },
          }),
        ),
    })),
  });
}

/** What `GET /figures` is asked for, one page at a time. The route is paged because Lambda's
 * response limit is 6 MB and an unpaged register at 50 sites was 3.3 MiB.
 *
 * 500 is the server's ceiling, so the fewest requests the API will answer; the server's own default
 * of 200 is for a caller that names nothing. */
export const FIGURES_PAGE = 500;

type FigurePage = components["schemas"]["FigurePage"];
type FigureStatusParam = "verified" | "awaiting" | "not_verified" | "gap" | "complete" | "broken";
type FigureOriginParam = "uploaded" | "manual" | "imported" | "connector";

/** One page of the register, tolerant of exactly one other shape: a bare array.
 *
 * That is what the API served before it paged, so it can only come from a server older than this
 * client during a deploy. It was **never partial** - it held every figure - so reading it as
 * complete is true, not generous. Any other body is a failed read, like every read here. */
function pageOf(body: unknown): FigurePage {
  if (Array.isArray(body)) {
    const items = body as Figure[];
    return {
      items,
      total: items.length,
      limit: items.length,
      offset: 0,
      truncated: false,
      next_offset: null,
      // Never partial, so the sum of its rows is the sum of the selection.
      whole_record_kgco2e: sumDecimals(items.map((figure) => figure.kgco2e)) ?? "0",
    };
  }
  const page = body as Partial<FigurePage> | null;
  if (!page || !Array.isArray(page.items) || typeof page.total !== "number") {
    throw new Error("The figures came back in a shape this screen cannot read.");
  }
  return page as FigurePage;
}

/** How many figures a drawer will list before it stops offering more and **says so**.
 *
 * A drawer's rows are buttons; ten thousand of them is a page nobody can read and a browser that
 * stalls. The reader pages in `FIGURES_PAGE` steps, and past this many the drawer states how many
 * exist and sends them to the register, which filters. The cap is on rows *listed*: every total on
 * screen is the server's and does not depend on it. */
export const FIGURES_DRAWER_CEILING = 5_000;

/** A cut of a window's figures, as a drawer asks for it. Every field only ever removes rows, on the
 * server (`GET /figures` applies them beside the engagement clamp and both ADR-0030 exclusions), so
 * a cut is always a subset of the window's register and its `total` counts that subset. */
export interface FigureCut {
  scope?: "1" | "2" | "3";
  category?: string;
  /** Only these sites. */
  facilityIds?: readonly string[];
  /** Every site except these: the tail the by-site chart gathers as "other sites". */
  excludeFacilityIds?: readonly string[];
  /** `A` is metered; `below_a` is every figure that is not. */
  grade?: "A" | "below_a";
  /** `unknown` is a figure with no tier. Name a scope with it, or Scope 1's rows all match. */
  marketTier?: string;
  /** Only the figures on the meters billed under this vendor's accounts (ADR-0062). */
  vendorId?: string;
  /** Scope 2 load an instrument did (`covered`) or did not (`fallback`) reach. */
  coverage?: "covered" | "fallback";
  /** Several categories at once (the table's filter chip); `category` is the drawer's single one. */
  categories?: readonly string[];
  /** Only these fuels or carriers, as the activity names them. */
  carriers?: readonly string[];
  /** Only these calendar months of the period's start, `YYYY-MM`. */
  months?: readonly string[];
  /** Only these data-quality grades. */
  qualities?: readonly string[];
  /** A search over site, category and carrier, applied on the server. */
  search?: string;
  /** Scope 2: only the figures whose market-based figure landed on one of these tiers. */
  instruments?: readonly string[];
  /** Derived on read, so the server derives it: `verified`, `awaiting`, `not_verified`, `gap`,
   * `complete` or `broken`. */
  statuses?: readonly string[];
  /** `uploaded`, `manual`, `imported` or `connector`. */
  origins?: readonly string[];
  /** A user's id, or `unowned` for a figure nobody answers for. */
  owner?: string;
  /** Whether the evidence chain holds. ANDed with every other term: Scope 3 lists only `complete`
   * (rule 3), and counts what it withholds with `broken`. */
  evidence?: "complete" | "broken";
  /** Order, most significant first. Only the columns the server can order by. */
  sorts?: readonly { column: FigureSortColumn; direction: "asc" | "desc" }[];
  /** Scope 2 location figures: carry the market-based figure of each record beside it. */
  includeMarket?: boolean;
}

/** The columns `GET /figures` orders by. A column derived on read (status, source, basis) is not here. */
export type FigureSortColumn = "tonnes" | "quantity" | "period" | "site" | "carrier" | "category" | "grade";

function cutKey(cut: FigureCut): string {
  return JSON.stringify([
    cut.scope ?? "",
    cut.category ?? "",
    [...(cut.facilityIds ?? [])].sort(),
    [...(cut.excludeFacilityIds ?? [])].sort(),
    cut.grade ?? "",
    cut.marketTier ?? "",
    cut.coverage ?? "",
    cut.vendorId ?? "",
    [...(cut.categories ?? [])].sort(),
    [...(cut.carriers ?? [])].sort(),
    [...(cut.months ?? [])].sort(),
    [...(cut.qualities ?? [])].sort(),
    cut.search ?? "",
    [...(cut.instruments ?? [])].sort(),
    [...(cut.statuses ?? [])].sort(),
    [...(cut.origins ?? [])].sort(),
    cut.owner ?? "",
    cut.evidence ?? "",
    // Order is part of the key and is *not* sorted: levels are ordered.
    (cut.sorts ?? []).map((level) => `${level.column}:${level.direction}`),
    cut.includeMarket ?? false,
  ]);
}

/** What a drawer reads: the figures loaded so far, **and how many there are**. */
export interface FigureCutRead {
  /** True until the first page answers - and for ever when no window was given (nothing asked). */
  isPending: boolean;
  error: unknown;
  refetch: () => void;
  figures: Figure[];
  /** The server's count of the cut. Zero only once a page has said so. */
  total: number;
  /** The cut's `kgco2e` added up **as whole records** (unallocated), over every row of the cut, in
   * kilograms - the server's exact string. `undefined` until a page has said so, and from a server
   * too old to state it: a sum is never made from the pages read. */
  wholeRecordKg: string | undefined;
  /** Whether more of the cut can be listed here (there are more, and the drawer ceiling is not hit). */
  canLoadMore: boolean;
  /** More exists but the drawer will not list it (`FIGURES_DRAWER_CEILING`). */
  ceilingReached: boolean;
  loadMore: () => void;
  loadingMore: boolean;
  /** A later page failed; the pages already loaded stay. */
  moreError: unknown;
}

/** What `GET /figures` is asked for a cut, without the paging: the one place a cut becomes a query. */
function figureQuery(window: Window, method: Scope2Method, cut: FigureCut) {
  return {
    period_start: window.period_start,
    period_end: window.period_end,
    scope2_method: method,
    ...(cut.scope ? { scope: cut.scope } : {}),
    ...(cut.category || cut.categories?.length
      ? { category: [...(cut.category ? [cut.category] : []), ...(cut.categories ?? [])] }
      : {}),
    ...(cut.facilityIds?.length ? { facility_id: [...cut.facilityIds] } : {}),
    ...(cut.excludeFacilityIds?.length
      ? { exclude_facility_id: [...cut.excludeFacilityIds] }
      : {}),
    ...(cut.grade ? { grade: cut.grade } : {}),
    ...(cut.marketTier ? { market_tier: cut.marketTier } : {}),
    ...(cut.vendorId ? { vendor_id: cut.vendorId } : {}),
    ...(cut.coverage ? { coverage: cut.coverage } : {}),
    ...(cut.carriers?.length ? { carrier: [...cut.carriers] } : {}),
    ...(cut.months?.length ? { month: [...cut.months] } : {}),
    ...(cut.qualities?.length
      ? { quality: [...cut.qualities] as ("A" | "B" | "C" | "D")[] }
      : {}),
    ...(cut.search?.trim() ? { q: cut.search.trim() } : {}),
    ...(cut.instruments?.length ? { instrument: [...cut.instruments] } : {}),
    ...(cut.statuses?.length
      ? { status: [...cut.statuses] as FigureStatusParam[] }
      : {}),
    ...(cut.origins?.length
      ? { origin: [...cut.origins] as FigureOriginParam[] }
      : {}),
    ...(cut.owner ? { owner: cut.owner } : {}),
    ...(cut.evidence ? { evidence: cut.evidence } : {}),
    ...(cut.sorts?.length
      ? {
          sort: cut.sorts.map((level) => level.column),
          direction: cut.sorts.map((level) => level.direction),
        }
      : {}),
    ...(cut.includeMarket ? { include_market: true } : {}),
  };
}

/** A cut of a window's figures, **one page at a time** - the rows a breakdown drawer lists.
 *
 * This is what lets a drawer open on a window of any size: it reads `FIGURES_PAGE` rows, says
 * *Showing N of M*, and reads the next page when asked, instead of loading the whole window and
 * filtering it in the browser (which failed visibly past ten thousand figures). The drawer's
 * headline is the server's total for the same cut and is never a sum over these rows.
 *
 * `window` is `undefined` when nothing is open: no request at all. */
export function useFiguresPaged(
  window: Window | undefined,
  method: Scope2Method,
  cut: FigureCut = {},
  options: {
    /** Keep the rows of the previous cut on screen while the next answers. For a table whose filter
     * or order just changed; a drawer opens on a cut of its own and must not show another's rows. */
    keepPrevious?: boolean;
    /** The most rows to list before saying so; default `FIGURES_DRAWER_CEILING`. */
    ceiling?: number;
  } = {},
): FigureCutRead {
  const listCeiling = options.ceiling ?? FIGURES_DRAWER_CEILING;
  const query = useInfiniteQuery({
    ...(options.keepPrevious ? { placeholderData: keepPreviousData } : {}),
    queryKey: keys.figuresCut(
      window?.period_start ?? "none",
      window?.period_end ?? "none",
      method,
      cutKey(cut),
    ),
    initialPageParam: 0,
    queryFn: window
      ? async ({ pageParam }) =>
          pageOf(
            unwrap(
              await api.GET("/api/v1/figures", {
                params: {
                  query: {
                    ...figureQuery(window, method, cut),
                    limit: FIGURES_PAGE,
                    offset: pageParam,
                  },
                },
              }),
            ),
          )
      : skipToken,
    getNextPageParam: (last) => last.next_offset ?? undefined,
  });

  const seen = new Set<string>();
  const figures: Figure[] = [];
  for (const page of query.data?.pages ?? []) {
    for (const figure of page.items) {
      if (!seen.has(figure.id)) {
        seen.add(figure.id);
        figures.push(figure);
      }
    }
  }
  const total = query.data?.pages[0]?.total ?? 0;
  const more = Boolean(query.hasNextPage);
  const ceilingReached = more && figures.length >= listCeiling;
  return {
    isPending: query.isPending,
    // A failure of the *first* page is the read failing; a later page failing keeps what is shown. A
    // refresh that failed is the read failing too: the rows still held are the last answer, not a
    // current one, and presenting them as current is the stale-data failure.
    error: query.data && !(query.isRefetchError && !query.isFetchNextPageError) ? null : query.error,
    refetch: () => void query.refetch(),
    figures,
    total,
    wholeRecordKg: query.data?.pages[0]?.whole_record_kgco2e,
    canLoadMore: more && !ceilingReached,
    ceilingReached,
    loadMore: () => void query.fetchNextPage(),
    loadingMore: query.isFetchingNextPage,
    moreError: query.data && query.isFetchNextPageError ? query.error : null,
  };
}

export type FigureFacets = components["schemas"]["FigureFacetsOut"];
export type QualityBySite = components["schemas"]["QualityBySiteOut"];
export type QualitySite = components["schemas"]["QualitySiteOut"];

/** What a figure list can be narrowed by in a window: the values present (sites, categories, carriers,
 * months, Scope 2 instruments), read with `DISTINCT` on the server rather than from the figures a
 * screen happens to hold. A paged list cannot offer its own filters from the page it has.
 *
 * `window` is `undefined` when nothing should be asked. */
export function useFigureFacets(
  window: Window | undefined,
  method: Scope2Method,
  scope?: "1" | "2" | "3",
) {
  return useQuery({
    queryKey: keys.figureFacets(
      window?.period_start ?? "none",
      window?.period_end ?? "none",
      method,
      scope,
    ),
    queryFn: window
      ? async () => {
          const facets = unwrap(
            await api.GET("/api/v1/figures/facets", {
              params: {
                query: {
                  period_start: window.period_start,
                  period_end: window.period_end,
                  scope2_method: method,
                  ...(scope ? { scope } : {}),
                },
              },
            }),
          );
          // A 200 whose body is not the facets (a proxy's page, an older server) is a failed read: a
          // filter line with no options must never be drawn as a window with nothing to choose from.
          if (!Array.isArray(facets.sites) || !Array.isArray(facets.categories) || !Array.isArray(facets.months)) {
            throw new Error("The filter options came back in a shape this screen cannot read.");
          }
          return facets;
        }
      : skipToken,
    placeholderData: keepPreviousData,
  });
}

/** Per site, how good one scope's figures are - figures at each grade, those awaiting acceptance and
 * those with a broken evidence chain - **counted over every figure on the server**. The Data quality
 * tab used to count the figures its browser had loaded, and refused past ten thousand. */
export function useQualityBySite(
  window: Window | undefined,
  method: Scope2Method,
  scope: "1" | "2" | "3",
) {
  return useQuery({
    queryKey: keys.qualityBySite(
      window?.period_start ?? "none",
      window?.period_end ?? "none",
      method,
      scope,
    ),
    queryFn: window
      ? async () =>
          unwrap(
            await api.GET("/api/v1/inventory/quality-by-site", {
              params: {
                query: {
                  period_start: window.period_start,
                  period_end: window.period_end,
                  scope2_method: method,
                  scope,
                },
              },
            }),
          )
      : skipToken,
  });
}

/** Every figure of a cut, read page by page - **for a file, never for a screen**.
 *
 * An export carries the whole selection (a file named after an inventory that held the page on screen
 * would be a different answer to the same question), so the button that makes one reads the cut to the
 * end at the moment it is pressed. Pages are asked for in the server's order, four at a time once the
 * count is known, and a window that changed between two pages leaves a repeated or a missing row that
 * the count check refuses to call a whole. */
export async function readEveryFigure(
  window: Window,
  method: Scope2Method,
  cut: FigureCut,
): Promise<Figure[]> {
  const ask = async (offset: number) =>
    pageOf(
      unwrap(
        await api.GET("/api/v1/figures", {
          params: { query: { ...figureQuery(window, method, cut), limit: FIGURES_PAGE, offset } },
        }),
      ),
    );
  const first = await ask(0);
  const byId = new Map<string, Figure>(first.items.map((figure) => [figure.id, figure]));
  const offsets: number[] = [];
  for (let at = first.next_offset ?? first.total; at < first.total; at += FIGURES_PAGE) offsets.push(at);
  for (let i = 0; i < offsets.length; i += 4) {
    const pages = await Promise.all(offsets.slice(i, i + 4).map(ask));
    for (const page of pages) for (const figure of page.items) byId.set(figure.id, figure);
  }
  if (byId.size !== first.total) {
    throw new Error(
      `The figures changed while they were being read (${String(byId.size)} of ${String(first.total)}). Try again.`,
    );
  }
  return [...byId.values()];
}

/** The largest figures of a window, **one page and no more** - a ranked excerpt, never a sum.
 *
 * For a panel that shows "the largest N of M" and nothing else needs the list: it asks the paged
 * route for its first page (`offset` 0, which is the server's order, largest first) and reads
 * `total` for the M. The KPI beside it is `useInventory`'s, so no screen needs every figure to
 * state a number. `undefined` for the window asks nothing. */
export function useFiguresTop(
  window: Window | undefined,
  method: Scope2Method,
  scope: "1" | "2" | "3" | undefined,
  rows: number,
) {
  return useQuery({
    queryKey: keys.figuresTop(
      window?.period_start ?? "none",
      window?.period_end ?? "none",
      method,
      scope,
      rows,
    ),
    queryFn: window
      ? async () => {
          const page = pageOf(
            unwrap(
              await api.GET("/api/v1/figures", {
                params: {
                  query: {
                    period_start: window.period_start,
                    period_end: window.period_end,
                    scope2_method: method,
                    ...(scope ? { scope } : {}),
                    limit: rows,
                    offset: 0,
                  },
                },
              }),
            ),
          );
          return { figures: page.items, total: page.total };
        }
      : skipToken,
  });
}

export type Activity = components["schemas"]["ActivityOut"];

/** One activity record: its figures and its revision history, for the record page.
 *
 * The header and the history come back together so the page does not render its title after its
 * content. Each tab fetches its own — the receipt per calculation, the work attached to the
 * record, the document behind it — because a tab nobody opens should cost nothing.
 */
export function useActivity(activityId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ["activity", activityId ?? "none"] as const,
    // **Gated on the record's kind, which costs a round trip and is worth it.** Asking for an
    // activity record by a risk's id is a legitimate 404 that this hook renders as "not a record
    // you can see" — and it is also a red 404 in the reader's console on every visit to a risk,
    // a site or a document. `make ui-check` fails a console error, and it is right to: a request
    // the product knows will fail is one it should not have made.
    enabled: Boolean(activityId) && enabled,
    queryFn: async (): Promise<Activity | null> => {
      if (!activityId) return null;
      const { data, error, response } = await api.GET("/api/v1/activities/{activity_id}", {
        params: { path: { activity_id: activityId } },
      });
      // 404 means "not a record you can see", which is a state to render rather than a failure.
      if (response.status === 404) return null;
      // **Anything else is thrown.** Swallowed, the record page said there is no such record in
      // this workspace — three specific claims, one of which an auditor would act on.
      if (error) throw refusalFrom(response, error, "That record could not be read.");
      return data ?? null;
    },
    retry: false,
  });
}

export type Entity = components["schemas"]["EntityOut"];

/** The legal entities this person may select (ADR-0035's shell).
 *
 * A selector, not a filter. Which entities come back is decided by the server under RLS — the
 * shell renders what it is given and never asks for one by id, because "which entity may I see"
 * is an authorization question and a URL is not allowed to answer it.
 */
export function useEntities() {
  return useQuery({
    queryKey: ["entities"] as const,
    queryFn: async (): Promise<Entity[]> => {
      const { data, error, response } = await api.GET("/api/v1/entities", {});
      if (error || !Array.isArray(data)) throw refusalFrom(response, error, "The companies could not be read.");
      return data;
    },
  });
}

export function useReceipt(calculationId: string | undefined, filingArtifactId?: string) {
  return useQuery({
    queryKey: keys.receipt(calculationId ?? "", filingArtifactId),
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/receipts/{calculation_id}", {
          params: {
            path: { calculation_id: calculationId as string },
            // **The issued filing the reader came from** (UX-02): the server then states the rules
            // version that filing was issued under. Absent, no filing is claimed.
            ...(filingArtifactId ? { query: { artifact_id: filingArtifactId } } : {}),
          },
        }),
      ),
    enabled: Boolean(calculationId),
    // **Cached forever only once it is historical** (ADR-0030).
    //
    // This used to be an unconditional `Infinity`, on the reasoning that a calculation is immutable.
    // Its *content* still is — the numbers, the factor, the components never change. What changed is
    // that a calculation can now stop being the figure in force, and `is_current` is that bit. A
    // receipt cached indefinitely would keep saying "current" after a restatement, which is the
    // exact failure ADR-0030 decision 4 exists to prevent, arriving from the cache instead of the
    // API.
    //
    // So: a restated receipt is terminal and cached forever. A current one is refetched on mount
    // like every other figure, because it is one.
    staleTime: (query) => (query.state.data?.is_current === false ? Infinity : 0),
  });
}

/** Every filing obligation in this workspace, with the reasoning behind each one.
 *
 * A **read**. Determining an obligation writes rows and a ledger entry, and a page load is not an
 * event that should do that — the determination is triggered by the facts changing.
 *
 * Refetched on mount rather than cached long, because an obligation changes when someone records a
 * turnover, and a stale "not in scope" beside a fresh figure is the class of error this product is
 * built to avoid.
 */
export function useObligations(enabled = true) {
  return useQuery({
    queryKey: keys.obligations(),
    enabled,
    queryFn: async () => unwrap(await api.GET("/api/v1/readiness/obligations", {})),
  });
}

/** What one **filing obligation** would have this entity state.
 *
 * Keyed by the obligation rather than by a regime and a window (ADR-0029). The old shape could not
 * say which of an organisation's filings it meant, so a group with a UK parent and a Californian
 * subsidiary got one organisation-wide answer for both — finding F02. The period, entity, boundary
 * and rules version now come back **on** the report, in `context`, because a figure whose scope the
 * reader has to infer is a figure they will infer wrongly.
 *
 * A **read**, and a preview rather than a filing: producing a hashed artifact pinned to a ledger
 * state is ADR-0020 and is not built. The figures come from the same calculations every other
 * screen reads, which is what makes a line's citation worth anything.
 *
 * Refetched on mount, like the other totals. A stale report beside a fresh figure is the failure
 * this product can least afford, and a report is the one place it would be quoted.
 */
export function useReport(obligationId: string | undefined, method: Scope2Method) {
  return useQuery({
    queryKey: keys.report(obligationId ?? "", method),
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/reports/obligations/{obligation_id}", {
          params: {
            path: { obligation_id: obligationId as string },
            query: { scope2_method: method },
          },
        }),
      ),
    enabled: Boolean(obligationId),
  });
}

export type Comparison = components["schemas"]["ComparisonOut"];
export type SiteMovement = components["schemas"]["SiteMovementOut"];

/** Why this filing's total differs from last year's (ACC-10).
 *
 * Its own query rather than a field on the report: it is a different question, it needs a second
 * window, and it must not reach the hashed artifact payload as though the regulation asked for it.
 * Keyed on the method too, because a Scope 2 movement is different under each.
 */
export function useReportComparison(obligationId: string | undefined, method: Scope2Method) {
  return useQuery({
    queryKey: ["report", "comparison", obligationId ?? "", method] as const,
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/reports/obligations/{obligation_id}/comparison", {
          params: {
            path: { obligation_id: obligationId as string },
            query: { scope2_method: method },
          },
        }),
      ),
    enabled: Boolean(obligationId),
  });
}

/** Record what the company says for one disclosure, and refresh the report it changes.
 *
 * A mutation rather than a query, and the invalidation is the point: a narrative statement is part
 * of the report, and whether the report **can be filed** changes the moment one is written. A
 * screen showing "cannot be filed — the methodologies statement has not been written" beside a
 * methodologies statement someone just wrote is the stale-figure failure ADR-0027 names, in its
 * most embarrassing form.
 */
export function useWriteStatement(
  regimeKey: string,
  obligationId: string | undefined,
  method: Scope2Method,
  window: Window,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      disclosureKey: string;
      statement?: string | null;
      denominator_value?: string | null;
      denominator_label?: string | null;
      /** The version of the statement the editor was opened with: `ReportLine.statement_version`, or
       * `0` for a statement not yet made (G-P9-04). The server refuses a stale one with 409
       * `stale_version`. */
      expectedVersion?: number;
    }) =>
      unwrap(
        await api.PUT("/api/v1/reports/{regime_key}/statements/{disclosure_key}", {
          params: {
            path: {
              regime_key: regimeKey,
              disclosure_key: input.disclosureKey,
            },
            // The statement belongs to the **filing's** period, which is the window the report
            // screen is showing — not to a calendar year this file used to assume.
            query: window,
          },
          body: {
            statement: input.statement ?? null,
            denominator_value: input.denominator_value ?? null,
            denominator_label: input.denominator_label ?? null,
            expected_version: input.expectedVersion,
          },
        }),
      ),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: keys.report(obligationId ?? "", method),
      }),
  });
}

/** Read the report again and answer with one disclosure line as it stands now.
 *
 * For a stale statement save: the editor has to say what the statement now reads and re-apply on the
 * version it is on, and neither may come from the line the editor was opened over.
 */
export function useReloadReportLine(obligationId: string | undefined, method: Scope2Method) {
  const queryClient = useQueryClient();
  const key = keys.report(obligationId ?? "", method);
  return async (disclosureKey: string): Promise<ReportLine | undefined> => {
    await queryClient.refetchQueries({ queryKey: key, exact: true });
    return queryClient.getQueryData<Report>(key)?.lines.find((line) => line.key === disclosureKey);
  };
}

// ------------------------------------------------------------------- filings (ADR-0020)

/** An issued filing as one version of its obligation (gate 24). The issue route answers the plain
 * `ArtifactOut`; the list answers this, with its place among the versions. */
export type Artifact = components["schemas"]["ArtifactVersionOut"];
export type Rederivation = components["schemas"]["RederivationOut"];

/** Every filing issued for one obligation, newest first.
 *
 * **A filing is not a report.** A report is rebuilt from today's figures on every request; a filing
 * is frozen, hashed and pinned to a ledger state. The screen has to keep them visibly apart, or the
 * word "report" ends up meaning two things and the one that matters is the one people stop
 * checking.
 */
export function useArtifacts(obligationId: string | undefined) {
  return useQuery({
    queryKey: ["artifacts", obligationId ?? "none"] as const,
    enabled: Boolean(obligationId),
    queryFn: async (): Promise<Artifact[]> => {
      if (!obligationId) return [];
      const { data, error, response } = await api.GET(
        "/api/v1/reports/obligations/{obligation_id}/artifacts",
        { params: { path: { obligation_id: obligationId } } },
      );
      // Swallowed, this read as *"nothing has been filed for this obligation yet"* — a statement
      // about the company's filing history, on the panel somebody checks before issuing another.
      if (error) throw refusalFrom(response, error, "The filings issued could not be read.");
      return data ?? [];
    },
  });
}

/** What issuing answered: the filing, and whether this call is the one that wrote it.
 *
 * **Two facts, because the server deliberately made them tellable** — `201` for a filing minted
 * here and `200` for an `Idempotency-Key` that had already issued one. Returning the artifact
 * alone would throw that away and leave the screen unable to say which happened, which is the
 * difference between "that is now filed" and "that was already filed".
 */
export interface Issued {
  artifact: components["schemas"]["ArtifactOut"];
  /** True when this request found an existing filing under its own key and wrote nothing. */
  replayed: boolean;
}

/** One attempt's key, in the alphabet the server validates (`^[A-Za-z0-9._:-]+$`, 8–80).
 *
 * `crypto.randomUUID` is only defined in a **secure context**, so it is present on https and on
 * localhost and absent over plain http — and the one place this is called is issuing a filing,
 * which is the worst place in the product for an undefined function. The fallback is not a
 * cryptographic claim and does not need to be: the key has to be unique within one organisation's
 * issuing history, not unguessable, because the server scopes it to `(org_id, key)` under RLS and
 * a caller cannot reach another tenant's. Two components so a collision needs both.
 */
function attemptKey(): string {
  const uuid = globalThis.crypto?.randomUUID?.();
  if (uuid) return `issue-${uuid}`;
  return `issue-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/** Issue a filing, **once per attempt the person started** (REP-06).
 *
 * `backend/app/api/reports.py` has taken an `Idempotency-Key` since REP-06 was built: the key
 * reaches a `pg_advisory_xact_lock` on `(org_id, key)` and a unique index, and the same key
 * arriving again returns the artifact it already produced instead of minting a second one. This
 * hook **sent no header at all**, so the protection existed on the most consequential write in the
 * product — bytes hashed into a chain that cannot take them back — and nothing asked for it.
 * Nothing failed, which is why it went unnoticed: the same shape as `Review.tsx` never sending
 * `expectedVersion`. A guard is not a guard until something calls it.
 *
 * **The key is minted per attempt and held until that attempt succeeds**, which is the only version
 * of this that is worth anything:
 *
 * - **Not one per click.** A fresh `randomUUID()` per press makes the header decorative — two
 *   presses would be two filings again, which is the thing it exists to prevent.
 * - **Not one per screen.** A deliberate restatement *is* a second filing (ADR-0020: nothing is
 *   edited and nothing is replaced), so a key that outlived a success would answer the second
 *   filing with the first and silently refuse a restatement somebody meant.
 * - So: minted when an attempt begins, reused by anything that happens to that attempt — a double
 *   press, a press while the first is still in flight, a press after a timeout whose outcome is
 *   unknown — and **cleared on success**, so the next press is a new filing.
 *
 * It is also re-minted when `obligationId` changes, because the server answers a key reused across
 * obligations with a `409` and a client defect. A reader who abandoned a failed attempt and moved
 * to another obligation would otherwise carry the stale key into it.
 *
 * **What this does not buy, and must not be described as buying:** safety across a page reload. The
 * key lives in a ref, so a reload mints a new one and the next press is a second filing. That is
 * why `Reports.tsx` still says *reload and look at the filings before sending it again* and still
 * withholds `retrySafe` — the sentence `retrySafe` prints would be false in exactly the case the
 * surrounding advice sends the reader into. Making it true needs a key that survives a reload, and
 * deriving one (obligation, method, ledger head) would make a deliberate restatement replay the
 * first filing instead. That is a filing decision, not a client detail, and it needs a decision
 * before it is built.
 */
export function useIssueArtifact(obligationId: string | undefined, method: Scope2Method) {
  const queryClient = useQueryClient();
  /** The attempt in progress: its key, and which obligation it is an attempt at. */
  const attempt = useRef<{ obligationId: string; key: string } | null>(null);
  return useMutation({
    mutationFn: async ({
      method,
      reissueReason,
    }: {
      method: Scope2Method;
      /** Why this is being issued again (D-17): sent only when given; the server requires it once an
       * earlier version exists and keeps it on the new row, outside the hash and the ledger. */
      reissueReason?: string;
    }): Promise<Issued> => {
      if (!obligationId) throw new Error("no filing to issue");
      if (attempt.current?.obligationId !== obligationId) {
        attempt.current = { obligationId, key: attemptKey() };
      }
      const { data, error, response } = await api.POST(
        "/api/v1/reports/obligations/{obligation_id}/artifacts",
        {
          params: {
            path: { obligation_id: obligationId },
            query: { scope2_method: method },
            header: { "Idempotency-Key": attempt.current.key },
          },
          ...(reissueReason ? { body: { reissue_reason: reissueReason } } : {}),
        },
      );
      if (error || !data) throw refusalFrom(response, error, "That filing could not be issued.");
      // **`200` means the key had already filed this**, so nothing was written here. Read from the
      // status rather than inferred from the row: an artifact is immutable, so a replay and a fresh
      // issue are indistinguishable in the body, and the screen would claim a filing it did not
      // make.
      return { artifact: data, replayed: response.status === 200 };
    },
    onSuccess: async () => {
      // **Cleared here and nowhere else.** A failure leaves the key in place so the next press is
      // the same attempt; a success retires it so the next press is a new filing.
      attempt.current = null;
      await queryClient.invalidateQueries({ queryKey: ["artifacts"] });
      // **And the report** (REP-02). Issuing moves the filing's verdict to `issued`, so a cache
      // that kept the preview showed "every check has passed" beside a filing that had just been
      // made — the screen disagreeing with the server about something a person had just done.
      // `make ui-check` asserts the headline moves, which is what found this.
      await queryClient.invalidateQueries({
        queryKey: keys.report(obligationId ?? "", method),
      });
    },
  });
}

/** Does today's data still produce this filing?
 *
 * Fetched on demand, never on render: it rebuilds the whole report on the server, and running that
 * for every row of a list would make opening a screen an expensive operation for an answer nobody
 * asked for.
 */
export function useRederivation(artifactId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ["artifacts", artifactId ?? "none", "rederivation"] as const,
    enabled: Boolean(artifactId) && enabled,
    retry: false,
    queryFn: async (): Promise<Rederivation | null> => {
      if (!artifactId) return null;
      const { data, error, response } = await api.GET(
        "/api/v1/artifacts/{artifact_id}/rederivation",
        { params: { path: { artifact_id: artifactId } } },
      );
      if (error) throw refusalFrom(response, error, "This filing could not be re-derived.");
      return data ?? null;
    },
  });
}


// ------------------------------------------------------ the spreadsheet round trip (S06)

export type InventoryDiff = components["schemas"]["DiffOut"];
export type DiffRow = components["schemas"]["DiffRowOut"];

/** The export's URL, for a plain link.
 *
 * A link rather than a fetch-and-blob: the browser's own download handling is better than
 * anything reimplemented here, the file is large, and an `<a download>` needs no state.
 */
export function exportUrl(window: Window): string {
  const params = new URLSearchParams({
    period_start: window.period_start,
    period_end: window.period_end,
  });
  return apiUrl(`/api/v1/inventory/export?${params.toString()}`);
}

/** The editable export of **exactly the rows a reader filtered to** (EXP-21).
 *
 * **A POST, and therefore a fetch rather than a link.** A selection is a list of ids; a list of ids
 * does not fit in a URL at any size worth having, and a GET whose query string a proxy truncates
 * would hand back a *different* subset than the one asked for — silently, and looking fine.
 *
 * **Ids rather than filters**, which is the whole design. The register filters on eight dimensions
 * in the browser, two of them derived (`status` from six evidence links, `q` across four fields).
 * Asking the server to re-apply them would be a second implementation of the same question, and
 * the two would eventually disagree about what the reader was looking at. The ids make the file
 * match the screen by construction.
 *
 * Returns the file's bytes and the name the server gave it, so the caller does not invent one.
 */
export async function fetchSelectionExport(
  window: Window,
  recordIds: readonly string[],
  format: "xlsx" | "csv",
): Promise<{ blob: Blob; filename: string }> {
  const params = new URLSearchParams({
    period_start: window.period_start,
    period_end: window.period_end,
    format,
  });
  const response = await apiFetch(`/api/v1/inventory/export?${params.toString()}`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ record_ids: recordIds }),
  });
  if (!response.ok) {
    // The 422 is the server refusing an empty selection rather than widening it to the whole
    // period. **The register cannot produce one** — with nothing selected the control is not
    // rendered, which is design rule 6 — so this is here for any other caller of this function,
    // and it is the sentence a reader would need rather than a status code.
    throw new Error(
      response.status === 422
        ? "No readings are selected, so there is nothing to export."
        : "That export could not be produced.",
    );
  }
  const disposition = response.headers.get("content-disposition") ?? "";
  const named = /filename="([^"]+)"/.exec(disposition);
  return {
    blob: await response.blob(),
    filename: named?.[1] ?? `emitiq-inventory-selection.${format}`,
  };
}

/** One column of the customer's file, and what we would read it as (Gate 38).
 *
 * `basis` says why: `name` (the header is one of ours), `alias` (a synonym we know), `conflict`
 * (it matches a column another header matches too, so **nothing is proposed**), `none` (it means
 * nothing to us). `suggestion` is a proposal for the person to confirm — never a mapping.
 *
 * Declared here rather than taken from the generated schema because the API carries it in a 422
 * refusal's `detail`, which OpenAPI does not model.
 */
export type ColumnProposal = {
  header: string;
  suggestion: string | null;
  basis: "name" | "alias" | "conflict" | "none";
  samples: string[];
};

export type ColumnsNeedMapping = {
  columns: ColumnProposal[];
  /** Our columns, and which of them a file cannot be read without. */
  targets: { name: string; required: boolean }[];
  missing: string[];
  conflicts: { target: string; headers: string[] }[];
};

/** A mapping a person confirmed: the file's header, to our column (`null` to leave it out). Sent
 * with the dry run **and** the apply, so the file that was looked at is read the way it was shown. */
export type ColumnMap = Record<string, string | null>;

/** Why a diff was refused — the **code**, not only the sentence (UX-04).
 *
 * `new Error(detail.message)` lost the one fact the screen has to branch on, and every refusal
 * then rendered as the same paragraph: *"That file could not be read."* For a 403 that sentence is
 * **false**, and falsely blames the reader's file for a permission they do not hold. UX-04 asks
 * that an unauthorized action fail explicitly; a refusal reported as a parse failure is the
 * opposite of explicit, and it sends somebody to re-save their spreadsheet.
 *
 * Same shape as `document.refusal_code` on the evidence path and for the same reason (DOC-01):
 * `message` is written to be reworded, `code` is the fact a client reads.
 */
export class DiffRefused extends Refused {
  /** The file's columns, when the refusal is *"say which column is which"* (`columns_need_mapping`).
   * Present only then: it is what turns a dead end into the mapping step. */
  readonly columns: ColumnsNeedMapping | null;

  constructor(
    status: number,
    code: string,
    message: string,
    columns: ColumnsNeedMapping | null = null,
  ) {
    super(status, code, message);
    this.name = "DiffRefused";
    this.columns = columns;
  }

  /** The file is fine and its columns are not ours: a person has to say which is which. Not a
   * fault in the file, and not something retrying fixes. */
  get needsMapping(): boolean {
    return this.code === "columns_need_mapping" && this.columns !== null;
  }

  /** The period is outside what this principal is engaged for — an assurer looking at a year they
   * were not engaged for. Not a permission on the *action*: a permission on the *window*, which is
   * a different sentence and a different thing to do about it. */
  get isOutsidePeriod(): boolean {
    return this.status === 404 || this.code === "outside_engagement";
  }
}

/** What an edited workbook **would** change. Writes nothing (XLS-02).
 *
 * A mutation rather than a query even though the server writes nothing: it is a `POST` with a file
 * body, it is not cacheable by anything sensible, and running it needs a person to choose a file.
 */
export function useInventoryDiff(window: Window) {
  return useMutation({
    mutationFn: async ({
      file,
      columnMap = null,
    }: {
      file: File;
      /** Absent, the file is read by its own column names. */
      columnMap?: ColumnMap | null;
    }): Promise<InventoryDiff> => {
      const body = new FormData();
      body.append("file", file);
      if (columnMap !== null) body.append("column_map", JSON.stringify(columnMap));
      const params = new URLSearchParams({
        period_start: window.period_start,
        period_end: window.period_end,
      });
      // `fetch` directly: multipart is one of the few things the generated client does not model,
      // and forcing it would mean lying to the types (same reason as `useUpload`).
      const response = await apiFetch(`/api/v1/inventory/diff?${params.toString()}`, {
        method: "POST",
        body,
      });
      if (!response.ok) {
        // **Parsed defensively, because a refusal is the one response most likely not to be JSON.**
        // A 413 from a proxy in front of the API, or a 502, arrives as HTML — and `response.json()`
        // then throws inside the mutation, which React Query surfaces as a `SyntaxError` about an
        // unexpected token. That is how "insufficient role" became "that file could not be read".
        const detail = await refusalDetail(response);
        throw new DiffRefused(
          response.status,
          detail.error,
          detail.message || `EmitIQ refused this file (${response.status}).`,
          detail.columns,
        );
      }
      return (await response.json()) as InventoryDiff;
    },
  });
}

export type InventoryApplied = components["schemas"]["AppliedOut"];
export type AppliedRow = components["schemas"]["AppliedRowOut"];

/** Apply an edited workbook (ADR-0039). **Writes**, and the same file twice writes once.
 *
 * It takes the **same `File` object the preview was run against**, not a re-chosen one. A second
 * pick would let the file change between looking and applying, and the thing a reviewer approved
 * would not be the thing that ran. The idempotency key is derived server-side from the row, so a
 * retry of *this* file is safe either way — but a reviewer approving one file and applying another
 * is not an idempotency problem and the key cannot save them from it.
 *
 * Every figure in the period changes, so the whole screen's data is invalidated rather than a
 * key: an apply creates superseding revisions, which moves totals, receipts, the census and the
 * filing verdict. Naming the keys would be a list to keep in step with every screen added later.
 */
/** What an apply request carries beyond the file — EXP-10's review.
 *
 * **`selection: null` is not an empty selection**, and the distinction is the reason this is a
 * discriminated field rather than an array that might be empty. Form data cannot tell an absent
 * list from an empty one, so the server takes `select_rows` as its own flag; a client that computed
 * an empty selection and sent no fields would otherwise be asking to apply *everything*. That is
 * `any([])` wearing a different hat, and this product has shipped that shape more than once.
 */
export type ApplyRequest = {
  file: File;
  /** The lines to apply, or `null` for the whole file. An empty array is refused by the server. */
  selection: number[] | null;
  /** One reason per high-risk row being written. Paired by position on the wire. */
  acknowledgements: ReadonlyArray<{ line: number; why: string }>;
  /** The mapping the preview was read with, or `null` if it was read by its own names. **Sent
   * again**, because the apply re-reads the file and must read it the way the reviewer saw it. */
  columnMap?: ColumnMap | null;
  /** An Admin's reason, sent only on the second attempt after `period_closed` (ADR-0094). Absent,
   * the server uses the acknowledgement reasons the rows already carry. */
  postCloseReason?: string;
  /** A person's answer for each group of new rows the preview asked a meter question about
   * (ADR-0103): the meter they chose, or `null` for "none of these". **`null` is an answer** - the
   * server holds the apply until every offered group has one - and it is not the same as leaving a
   * group out of this list, which is what an unanswered group looks like. Never built from a cell
   * of the file. */
  meterChoices?: ReadonlyArray<{
    facility_id: string;
    category: string;
    fuel_or_carrier: string;
    meter_id: string | null;
  }>;
};

export function useInventoryApply(window: Window) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      file,
      selection,
      acknowledgements,
      columnMap = null,
      postCloseReason,
      meterChoices = [],
    }: ApplyRequest): Promise<InventoryApplied> => {
      const body = new FormData();
      body.append("file", file);
      if (postCloseReason) body.append("post_close_reason", postCloseReason);
      //: Only when somebody answered something: a file with no meter question sends the request it
      //: always did.
      if (meterChoices.length > 0) body.append("meter_choices", JSON.stringify(meterChoices));
      if (columnMap !== null) body.append("column_map", JSON.stringify(columnMap));
      if (selection !== null) {
        body.append("select_rows", "true");
        for (const line of selection) body.append("apply_line", String(line));
      }
      // **Appended in lockstep.** The server pairs them by position and refuses a length mismatch
      // rather than zipping short, because a short zip attaches a reason to the wrong correction.
      for (const { line, why } of acknowledgements) {
        body.append("acknowledge_line", String(line));
        body.append("acknowledge_reason", why);
      }
      const params = new URLSearchParams({
        period_start: window.period_start,
        period_end: window.period_end,
      });
      const response = await apiFetch(`/api/v1/inventory/apply?${params.toString()}`, {
        method: "POST",
        body,
      });
      if (!response.ok) {
        // A file that reaches a closed period keeps the periods and the role fact (ADR-0094): the
        // screen offers an Admin the reason box from them, and `DiffRefused` carries neither.
        if (response.status === 409) {
          const closed: unknown = await response.clone().json().catch(() => null);
          const refused = refusalFrom(response, closed, "That file could not be applied.");
          if (isPeriodClosed(refused)) throw refused;
        }
        const detail = await refusalDetail(response);
        throw new DiffRefused(
          response.status,
          detail.error,
          detail.message || `EmitIQ refused this file (${response.status}).`,
        );
      }
      return (await response.json()) as InventoryApplied;
    },
    onSuccess: () => queryClient.invalidateQueries(),
  });
}

// ------------------------------------------------- saved column mappings (ADR-0104)

/** A mapping saved for a file with exactly these columns, keyed by **this** file's headers.
 *
 * An offer, never a decision: the mapping step shows it and the person presses a button to fill the
 * selects, then confirms the comparison as they would have without it. */
export type SavedColumnMapping = { id: string; name: string; mapping: ColumnMap };

/** Is there a saved mapping for exactly this set of columns? Sends the header names and nothing
 * else - never a row or a sample. `null` is the ordinary answer, and it means "none is saved".
 *
 * **A failed lookup throws.** It used to be `null` too, so "the lookup failed" read as "no saved
 * mapping" (whole-product review 2026-11, finding 12). The step still works without the offer - the
 * screen shows a sentence beside the selects and does not block the mapping the person can make by
 * hand - but it no longer claims there was nothing to offer. */
export function useSavedColumnMapping(headers: string[]) {
  return useQuery({
    queryKey: ["column-mapping-match", headers],
    queryFn: async (): Promise<SavedColumnMapping | null> => {
      const response = await apiFetch("/api/v1/inventory/column-mappings/match", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ headers }),
      });
      if (!response.ok) {
        throw new Error(`Saved column mappings could not be checked (${response.status}).`);
      }
      const payload = (await response.json()) as { offer: SavedColumnMapping | null };
      return payload.offer ?? null;
    },
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });
}

/** Remember the mapping a comparison has just accepted. Fire-and-report: the comparison already
 * ran, so a failure here is a sentence beside the result, not a reason to withhold it. */
export async function saveColumnMapping(name: string, mapping: ColumnMap): Promise<string | null> {
  try {
    const response = await apiFetch("/api/v1/inventory/column-mappings", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ name, mapping }),
    });
    if (response.ok) return null;
    const detail = await refusalDetail(response);
    return detail.message || `The mapping could not be saved (${response.status}).`;
  } catch {
    return "The mapping could not be saved.";
  }
}

/** Forget a saved mapping. `true` when it is gone (or was already). */
export async function forgetColumnMapping(id: string): Promise<boolean> {
  try {
    const response = await apiFetch(`/api/v1/inventory/column-mappings/${id}`, { method: "DELETE" });
    return response.ok || response.status === 404;
  } catch {
    return false;
  }
}

async function refusalDetail(
  response: Response,
): Promise<{ error: string; message: string; columns: ColumnsNeedMapping | null }> {
  try {
    const payload = (await response.json()) as {
      detail?: ({ error?: string; message?: string } & Partial<ColumnsNeedMapping>) | string;
    };
    const detail = payload.detail;
    if (typeof detail === "string") return { error: "", message: detail, columns: null };
    return {
      error: detail?.error ?? "",
      message: detail?.message ?? "",
      // Only when the refusal is the mapping one *and* carries the lists the step needs: a body
      // that says so and brings no columns is not a mapping step, it is a sentence.
      columns:
        detail?.error === "columns_need_mapping" &&
        Array.isArray(detail.columns) &&
        Array.isArray(detail.targets)
          ? {
              columns: detail.columns,
              targets: detail.targets,
              missing: detail.missing ?? [],
              conflicts: detail.conflicts ?? [],
            }
          : null,
    };
  } catch {
    // Deliberately empty rather than invented: the caller turns a missing message into one naming
    // the status, which is true, where a guess at the reason would not be.
    return { error: "", message: "", columns: null };
  }
}

// ------------------------------------------------------------- members (S11, ADR-0008)

export type Member = components["schemas"]["MemberOut"];
export type MemberRole = components["schemas"]["Role"];

/** Everyone in this workspace. Admin-only on the server; a non-admin's request 401s or 403s.
 *
 * `retry: false` because a 403 is an answer, not a failure — retrying it three times on every
 * render would be three refusals for a question already answered.
 */
export function useMembers(enabled: boolean) {
  return useQuery({
    queryKey: ["members"] as const,
    enabled,
    retry: false,
    queryFn: async (): Promise<Member[]> => {
      const { data, error, response } = await api.GET("/api/v1/members", {});
      // Swallowed, this read as *"nobody else is in this workspace yet"* — the most misleading
      // empty state in the product, on the screen that governs who may approve a filing.
      if (error || !Array.isArray(data)) throw refusalFrom(response, error, "The people in this workspace could not be read.");
      return data;
    },
  });
}

export type EngagementReach = components["schemas"]["EngagementReachOut"];

/** Which companies and sites an engaged auditor reads - ADR-0093, gate 16.
 *
 * **Never defaults, never succeeds with nothing.** A failed read throws, so `Loaded` can tell a
 * refusal from "this engagement reaches no site", which is a finding an Admin must see. `enabled` is
 * the Admin's click: the answer is computed from the auditor's policies on every call, so it is not
 * fetched for a roster the Admin has not asked about.
 */
export function useEngagementReach(userId: string, enabled: boolean) {
  return useQuery({
    queryKey: ["members", "engagement-reach", userId] as const,
    enabled,
    retry: false,
    // An answer about who reads what is only as good as the last change to the ownership history.
    staleTime: 0,
    queryFn: async (): Promise<EngagementReach> => {
      const { data, error, response } = await api.GET(
        "/api/v1/members/{user_id}/engagement-reach",
        { params: { path: { user_id: userId } } },
      );
      if (error || !data) {
        throw refusalFrom(response, error, "What this auditor can read could not be worked out.");
      }
      return data;
    },
  });
}

export type MemberStanding = components["schemas"]["MemberStandingOut"];

/** One member's second-factor state, joining dates and decision counts - Admin only.
 *
 * **Never defaults, never succeeds with nothing**: a failed read throws, so `Loaded` can tell a
 * refusal from a person with no second factor and no decisions. `enabled` is the page's own
 * Admin check; a non-Admin does not ask, because the server would refuse and the refusal is not a
 * thing to retry. Not cached beyond the visit - an enrolment that happened a minute ago is what the
 * Admin came to see.
 */
export function useMemberStanding(userId: string, enabled: boolean) {
  return useQuery({
    queryKey: ["members", "standing", userId] as const,
    enabled,
    retry: false,
    staleTime: 0,
    queryFn: async (): Promise<MemberStanding> => {
      const { data, error, response } = await api.GET("/api/v1/members/{user_id}/standing", {
        params: { path: { user_id: userId } },
      });
      if (error || !data) {
        throw refusalFrom(response, error, "This person's standing could not be read.");
      }
      return data;
    },
  });
}

export function useUpdateMember() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      userId: string;
      role?: MemberRole;
      status?: "active" | "suspended";
    }) => {
      const { data, error, response } = await api.PATCH("/api/v1/members/{user_id}", {
        params: { path: { user_id: input.userId } },
        body: { role: input.role ?? null, status: input.status ?? null },
      });
      if (error) {
        throw refusalFrom(response, error, "That person's membership could not be changed.");
      }
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["members"] });
    },
  });
}

// --------------------------------------------------- administration workspace (S11, EXP-22)

export type AdminSection = components["schemas"]["SectionOut"];

/** What is in force in this workspace, and what is not built.
 *
 * Admin-only on the server, so `enabled` gates it the same way `useMembers` does and `retry` is
 * off: a 403 is an answer.
 */
export function useAdministration(enabled: boolean) {
  return useQuery({
    queryKey: ["administration"] as const,
    enabled,
    retry: false,
    queryFn: async () => {
      const { data, error, response } = await api.GET("/api/v1/administration", {});
      if (error) throw refusalFrom(response, error, "This workspace's settings could not be read.");
      return data ?? null;
    },
  });
}

// ------------------------------------------------------- inventory close (S09, CLS-01)

export type Reconciliation = components["schemas"]["ReconciliationOut"];
export type CloseBlocker = components["schemas"]["BlockerOut"];

/** What is outstanding before this period can be closed.
 *
 * Readable by anyone who can read the inventory, because it is the list a contributor works
 * through — closing is an Approver's act; knowing what is left is not.
 */
export function useReconciliation(periodId: string | undefined) {
  return useQuery({
    queryKey: ["reconciliation", periodId ?? "none"] as const,
    enabled: Boolean(periodId),
    queryFn: async (): Promise<Reconciliation | null> => {
      if (!periodId) return null;
      const { data, error, response } = await api.GET("/api/v1/periods/{period_id}/reconciliation", {
        params: { path: { period_id: periodId } },
      });
      // **Thrown.** Both consumers branch on `error`, but a read that cannot fail answered every
      // refusal with `null` - "nothing outstanding" to anything that read `data` alone.
      if (error) throw refusalFrom(response, error, "What is outstanding for this period could not be read.");
      return data ?? null;
    },
  });
}

/** Close the period, or come back with everything that is outstanding.
 *
 * A 409 carries the **whole reconciliation**, not a message, so the screen can list every blocker
 * at once — a refusal naming one problem costs a customer an attempt per outstanding item.
 */
export function useClosePeriod(periodId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    // `expectedClosureSeq` is the number of closes the screen was looking at (ADR-0094 C3). Optional
    // on a close — the server's row lock already makes a stale close safe — but sent when the lock
    // was read, so two people closing from one screen get `stale_closure` rather than a second event.
    mutationFn: async (input?: { expectedClosureSeq?: number }): Promise<Reconciliation> => {
      if (!periodId) throw new Error("no period to close");
      const { data, error, response } = await api.POST("/api/v1/periods/{period_id}/close", {
        params: { path: { period_id: periodId } },
        body: { expected_closure_seq: input?.expectedClosureSeq ?? null },
      });
      if (error || !data) throw refusalFrom(response, error, "That period could not be closed.");
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["reconciliation"] });
      await queryClient.invalidateQueries({ queryKey: ["period-lock"] });
      // The period's status changes, and the workspace carries it.
      await queryClient.invalidateQueries({ queryKey: keys.organization() });
    },
  });
}

/** The verification pack for one filing — **ASR-03**.
 *
 * A URL rather than a query, for the same reason `exportUrl` is: the answer is a file a person
 * saves, and fetching it into memory to hand it to a download would be work the browser already
 * does better.
 */
export function verificationPackUrl(
  obligationId: string,
  method: Scope2Method,
): string {
  return apiUrl(`/api/v1/reports/obligations/${obligationId}/verification-pack?scope2_method=${method}`);
}

// ------------------------------------- testing a package before it is in force (REP-08)

export type PackagePreflight = components["schemas"]["PackagePreflightOut"];
export type PackageChange = components["schemas"]["PackageChangeOut"];
export type PackageFinding = components["schemas"]["PackageFindingOut"];

/** What activating the proposed rules package would change, against this filing's own data.
 *
 * **`enabled` is the whole shape of this hook, and it carries two facts rather than one.**
 *
 * - **The role.** The route is Approver and Admin only, so a Contributor's browser must never send
 *   it: a 403 the client could have predicted is the defect UX-04 names, one layer down from the
 *   button that produces it. `retry: false` for the same reason `useAdministration` has it — a 403
 *   is an answer, and asking again is a control that predictably fails.
 * - **The press.** A preflight is something a person *runs*, and the response says so: its
 *   `activation_is_not_here` is written for *"the thing they have just run"*. Fetching it on every
 *   open of the Reports screen would make that sentence address nobody, and it would put a diff of
 *   a package nobody has proposed changing in front of a reader who came to issue a filing.
 *
 * **Not cached long, deliberately.** The proposed half is fixed by the deployed source tree, but
 * every line in it is exercised **against real figures** — so the answer goes stale the moment
 * somebody records an activity record, and a cached "produced no figure" beside a figure that now
 * exists is the stale-figure failure in the one place it would be read as a reason not to ship.
 */
export function usePackagePreflight(obligationId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ["package-preflight", obligationId ?? "none"] as const,
    enabled: Boolean(obligationId) && enabled,
    retry: false,
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/reports/obligations/{obligation_id}/package-preflight", {
          params: { path: { obligation_id: obligationId as string } },
        }),
      ),
  });
}

// --------------------------------------------- telling us what happened (ADR-0036, EXP-07)

export type ManualEntry = components["schemas"]["ManualEntryIn"];
export type ManualEntryResult = components["schemas"]["ManualEntryOut"];

/** Record a figure on a person's attestation, with no document behind it (ADR-0036).
 *
 * **No `attested_by`.** The attester is the signed-in principal and the server will not take one
 * from the body: an attestation is the caller's own statement, and letting one person record
 * another's makes the audit trail a claim about a claim.
 *
 * Invalidates everything, like an apply does: a new activity record moves totals, the census, the
 * evidence claim on the home screen and the filing verdict. Naming the keys would be a list to keep
 * in step with every screen added later.
 */
export function useRecordManualEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: ManualEntry): Promise<ManualEntryResult> => {
      const { data, error, response } = await api.POST(
        "/api/v1/inventory/activities/manual",
        { body: input },
      );
      if (error || !data) {
        // The API's own `detail.message` where there is one — these are written to be read by the
        // person who typed the figure, and replacing them with a generic sentence is what made a
        // 403 render as "that file could not be read" on the bulk screen.
        throw refusalFrom(response, error, "That could not be recorded.");
      }
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries(),
  });
}

export type MeterChoice = components["schemas"]["MeterChoiceOut"];

/** The meters a person could say a hand-typed figure came off (ADR-0099).
 *
 * Empty for most sites — only a meter with a live stream declared for it, for this kind of
 * activity and fuel, is listed — and then the form shows no control. Enabled once a site and a kind
 * of activity are chosen; the fuel narrows the list when it is. The choice is optional, so a failed
 * read costs only the picker and the gap stays visible, which is the honest outcome.
 */
export function useManualMeterChoices(
  facilityId: string,
  category: string,
  carrier: string,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ["inventory", "meter-choices", facilityId, category, carrier] as const,
    enabled: facilityId !== "" && category !== "" && enabled,
    retry: false,
    queryFn: async (): Promise<MeterChoice[]> => {
      const { data, error, response } = await api.GET("/api/v1/inventory/meter-choices", {
        params: {
          query: {
            facility_id: facilityId,
            category,
            ...(carrier ? { fuel_or_carrier: carrier } : {}),
          },
        },
      });
      if (error) throw refusalFrom(response, error, "This site’s meters could not be read.");
      return data?.meters ?? [];
    },
  });
}


// ------------------------------------------------- the climate risk register (EXP-17)

export type Risk = components["schemas"]["RiskOut"];
export type RiskAction = components["schemas"]["ActionOut"];
export type RiskScale = components["schemas"]["ScaleOut"];

/** The register. Open risks by default; closed ones stay readable on request. */
export function useRisks(includeClosed = false, enabled = true) {
  return useQuery({
    queryKey: ["risks", includeClosed] as const,
    // `enabled`, because the record page asks for the register only when it is showing a risk —
    // and the page is reached by nine kinds. A hook called inside an `if` is the one React rule
    // that fails at runtime rather than at compile time, so the call stays unconditional.
    enabled,
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/risks", { params: { query: { include_closed: includeClosed } } }),
      ),
  });
}

/** Every scale, superseded ones included — a historic score was read on one of them. */
export function useRiskScales() {
  return useQuery({
    queryKey: ["risks", "scales"] as const,
    queryFn: async () => unwrap(await api.GET("/api/v1/risks/scales", {})),
  });
}

/** Put a risk on the register. **An Approver's act**: a register is the company's own account of
 * what it is exposed to, and adding a row to it is making the statement rather than preparing it.
 */
export function useRecordRisk() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: components["schemas"]["RiskIn"]): Promise<Risk> => {
      const { data, error, response } = await api.POST("/api/v1/risks", { body: input });
      if (error || !data) {
        throw refusalFrom(response, error, "That could not be recorded.");
      }
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["risks"] }),
  });
}

/** Add a response action. The risk moves to `responding` by itself, server-side. */
export function useAddRiskAction(riskId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: components["schemas"]["ActionIn"]): Promise<Risk> => {
      const { data, error, response } = await api.POST("/api/v1/risks/{risk_id}/actions", {
        params: { path: { risk_id: riskId } },
        body: input,
      });
      if (error || !data) {
        throw refusalFrom(response, error, "That could not be added.");
      }
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["risks"] }),
  });
}

/** Close a risk, with a reason. Refused server-side while any action is still open. */
export function useCloseRisk(riskId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (reason: string): Promise<Risk> => {
      const { data, error, response } = await api.POST("/api/v1/risks/{risk_id}/close", {
        params: { path: { risk_id: riskId } },
        body: { reason },
      });
      if (error || !data) {
        throw refusalFrom(response, error, "That could not be closed.");
      }
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["risks"] }),
  });
}

/** Define the scoring scale, or replace one (`replaces`). **An Admin's act** (D-13, ADR-0230). Replacing
 * rescores nothing: the risks scored on the old scale are marked as owing a rescoring (RSK-02). */
export function useDefineRiskScale() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: components["schemas"]["ScaleIn"]): Promise<RiskScale> => {
      const { data, error, response } = await api.POST("/api/v1/risks/scales", { body: input });
      if (error || !data) {
        throw refusalFrom(response, error, "That scale could not be saved.");
      }
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["risks"] }),
  });
}

/** Assess a risk again: a new dated assessment, never an overwrite (ADR-0230). */
export function useRescoreRisk(riskId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: components["schemas"]["RescoreIn"]): Promise<Risk> => {
      const { data, error, response } = await api.POST("/api/v1/risks/{risk_id}/rescore", {
        params: { path: { risk_id: riskId } },
        body: input,
      });
      if (error || !data) {
        throw refusalFrom(response, error, "That assessment could not be recorded.");
      }
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["risks"] }),
  });
}

/** Reopen a closed risk, with a reason. The closure stays in the risk's history (ADR-0230). */
export function useReopenRisk(riskId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (reason: string): Promise<Risk> => {
      const { data, error, response } = await api.POST("/api/v1/risks/{risk_id}/reopen", {
        params: { path: { risk_id: riskId } },
        body: { reason },
      });
      if (error || !data) {
        throw refusalFrom(response, error, "That could not be reopened.");
      }
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["risks"] }),
  });
}

/** Replace a stored "complete when" or "review if" text, under the placeholder rule (D-09). */
export function useRewordRisk(riskId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: components["schemas"]["WordingIn"]): Promise<Risk> => {
      const { data, error, response } = await api.POST("/api/v1/risks/{risk_id}/wording", {
        params: { path: { risk_id: riskId } },
        body: input,
      });
      if (error || !data) {
        throw refusalFrom(response, error, "That wording could not be saved.");
      }
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["risks"] }),
  });
}

// ------------------------------------------------ the transition plan (EXP-20)

export type TransitionPlan = components["schemas"]["PlanOut"];
export type PlanTarget = components["schemas"]["TargetOut"];
export type PlanInitiative = components["schemas"]["InitiativeOut"];
export type PlanDecision = components["schemas"]["DecisionOut"];

/** The whole plan, in one call.
 *
 * One call rather than six because a screen that fetched the targets and forgot the decisions would
 * show a plan that looks settled — the same reason `ReportingContext` travels whole.
 */
export function usePlan(enabled = true) {
  return useQuery({
    queryKey: ["plan"] as const,
    enabled,
    queryFn: async () => unwrap(await api.GET("/api/v1/plan", {})),
  });
}

/** Add a delivery action with what it is **expected** to save.
 *
 * There is no achieved field here on purpose: achieving something is a separate act with a separate
 * verb, because a form taking both at once is a form where somebody types their hope into the
 * measured column.
 */
export function useAddInitiative() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: components["schemas"]["InitiativeIn"]): Promise<TransitionPlan> => {
      const { data, error, response } = await api.POST("/api/v1/plan/initiatives", { body: input });
      if (error || !data) {
        throw refusalFrom(response, error, "That could not be added.");
      }
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["plan"] }),
  });
}

export type PlanTargetIn = components["schemas"]["TargetIn"];
export type PlanAssumptionsIn = components["schemas"]["AssumptionsIn"];
export type PlanReassessIn = components["schemas"]["ReassessIn"];
export type PlanScenarios = components["schemas"]["ScenarioOut"];
export type PlanProjection = components["schemas"]["ProjectionOut"];

/** Commit to a target, or write the next version of one (ACT-01).
 *
 * **A baseline that differs from the last version comes back `needs_reassessment` with a task
 * raised** (ADR-0108): the screen reads that from the response's `standing`, never from a
 * comparison it makes itself, because two copies of "did the baseline change" eventually disagree.
 * Invalidates every read a new version or a new baseline changes: the plan, the history, the
 * scenarios (their baseline is this target's) and the work queue the task lands in.
 */
export function useSetTarget() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: PlanTargetIn): Promise<TransitionPlan> => {
      const { data, error, response } = await api.POST("/api/v1/plan/targets", { body: input });
      if (error || !data) {
        throw refusalFrom(response, error, "That could not be recorded.");
      }
      return data;
    },
    onSuccess: () => invalidatePlanReads(queryClient),
  });
}

/** Say what a target now is after its baseline moved. Writes the next version; edits none. */
export function useReassessTarget() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { key: string } & PlanReassessIn): Promise<TransitionPlan> => {
      const { key, ...body } = input;
      const { data, error, response } = await api.POST("/api/v1/plan/targets/{key}/reassess", {
        params: { path: { key } },
        body,
      });
      if (error || !data) {
        throw refusalFrom(response, error, "That could not be recorded.");
      }
      return data;
    },
    onSuccess: () => invalidatePlanReads(queryClient),
  });
}

/** Record a new version of the forecast inputs. Versioned, never edited. */
export function useRecordAssumptions() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: PlanAssumptionsIn): Promise<TransitionPlan> => {
      const { data, error, response } = await api.POST("/api/v1/plan/assumptions", {
        body: input,
      });
      if (error || !data) {
        throw refusalFrom(response, error, "That could not be recorded.");
      }
      return data;
    },
    onSuccess: () => invalidatePlanReads(queryClient),
  });
}

function invalidatePlanReads(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: ["plan"] });
  void queryClient.invalidateQueries({ queryKey: ["plan-history"] });
  void queryClient.invalidateQueries({ queryKey: ["plan-scenarios"] });
  void queryClient.invalidateQueries({ queryKey: ["work"] });
}

/** Every version of one target, oldest first. The original is still there, unchanged. */
export function useTargetHistory(key: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ["plan-history", key] as const,
    enabled: enabled && key !== undefined,
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/plan/targets/{key}/history", {
          params: { path: { key: key ?? "" } },
        }),
      ),
  });
}

/** What each version of the assumptions makes the target's baseline become — modelled, computed on
 * the plan's own figures, and never the inventory (ACT-03). */
export function useScenarios(targetKey: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ["plan-scenarios", targetKey] as const,
    enabled: enabled && targetKey !== undefined,
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/plan/scenarios", {
          params: { query: { target_key: targetKey ?? null } },
        }),
      ),
  });
}

/** Log a decision about the plan. Append-only, server-side. */
export function useRecordDecision() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: components["schemas"]["PlanDecisionIn"]): Promise<TransitionPlan> => {
      const { data, error, response } = await api.POST("/api/v1/plan/decisions", { body: input });
      if (error || !data) {
        throw refusalFrom(response, error, "That could not be recorded.");
      }
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["plan"] }),
  });
}
